import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import * as bannerServices from "../../../services/banner.services";
import * as pujaServices from "../../../services/puja.services";
import * as reportServices from "../../../services/report.services";
import * as psychicServices from "../../../services/psychic.services";
import * as productServices from "../../../services/product.services";
import * as appMenuServices from "../../../services/appMenu.services";
import { SWAL_SETTINGS, INPUT_LENGTH_100, INPUT_LENGTH_500 } from "../../../../../utils/Constants";
import {
    globalLoader,
    handleServerValidations,
} from "../../../../../utils/commonfunction";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

const BannerAdd = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [previewimage, setPreviewImage] = useState("");

    const [pujas, setPujas] = useState([]);
    const [reports, setReports] = useState([]);
    const [astrologers, setAstrologers] = useState([]);
    const [products, setProducts] = useState([]);
    const [freeServices, setFreeServices] = useState([]);

    const breadcrumbs = [
        { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
        { title: t("Banners"), url: "/admin/cms/banner/list/1" },
        { title: t("Add Banners"), url: "" },
    ];

    useEffect(() => {
        // Fetch Pujas
        pujaServices.List({ page: 1, per_page: 500 })
            .then(res => setPujas(res?.data?.list || res?.data || []))
            .catch(err => console.error(err));

        // Fetch Reports
        reportServices.List({ page: 1, per_page: 500 })
            .then(res => setReports(res?.data?.list || res?.data || []))
            .catch(err => console.error(err));

        // Fetch Astrologers
        psychicServices.GetAllAstrologer()
            .then(res => setAstrologers(res?.data || []))
            .catch(err => console.error(err));

        // Fetch Products
        productServices.List({ page: 1, per_page: 500 })
            .then(res => setProducts(res?.data?.list || res?.data || []))
            .catch(err => console.error(err));

        // Fetch Free Services (App Menu Items)
        appMenuServices.ListItems({ page: 1, per_page: 500 })
            .then(res => setFreeServices(res?.data?.list || res?.data || []))
            .catch(err => console.error(err));
    }, []);

    const validationSchema = Yup.object().shape({
        title: Yup.string()
            .trim()
            .required(t("Title is required"))
            .max(INPUT_LENGTH_100, t("Title is too long")),
        description: Yup.string()
            .trim()
            .required(t("Description is required"))
            .max(INPUT_LENGTH_500, t("Description is too long")),
        type: Yup.string()
            .required(t("Type is required")),
        redirect_type: Yup.string().nullable(),
        redirect_id: Yup.string().nullable(),
        url: Yup.string()
            .trim()
            .max(INPUT_LENGTH_500, t("URL is too long"))
            .nullable(),
        banner_image: Yup.mixed()
            .required(t("Banner image is required"))
            .test("fileSize", t("File size is too large (max 5MB)"), (value) => !value || (value && value.size <= 5 * 1024 * 1024))
            .test("fileType", t("Unsupported file format"), (value) => !value || (value && ["image/jpg", "image/jpeg", "image/png", "image/webp"].includes(value.type))),
    });

    const formik = useFormik({
        initialValues: {
            title: "",
            description: "",
            type: "",
            redirect_type: "",
            redirect_id: "",
            url: "",
            banner_image: null,
            status: 1,
        },
        validationSchema: validationSchema,
        onSubmit: (values, { setSubmitting }) => {
            setSubmitted(true);
            globalLoader(true);
            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("description", values.description);
            formData.append("status", values.status);
            formData.append("type", values.type);
            formData.append("redirect_type", values.redirect_type || "");
            formData.append("redirect_id", values.redirect_id || "");
            formData.append("url", values.url || (values.redirect_type && values.redirect_id ? `/${values.redirect_type}/${values.redirect_id}` : ""));
            if (values.banner_image) {
                formData.append("banner_image", values.banner_image);
            }

            bannerServices
                .Add(formData)
                .then((response) => {
                    setSubmitting(false);
                    globalLoader(false);
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        setTimeout(() => {
                            navigate(`/admin/cms/banner/list/1`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                        setSubmitted(false);
                    }
                })
                .catch((error) => {
                    globalLoader(false);
                    setSubmitted(false);
                    console.log("error ====> ", error);
                });
        },
    });

    const handleRedirectTypeChange = (e) => {
        const val = e.target.value;
        formik.setFieldValue("redirect_type", val);
        formik.setFieldValue("redirect_id", "");
        if (val) {
            formik.setFieldValue("url", `/${val}`);
        } else {
            formik.setFieldValue("url", "");
        }
    };

    const handleRedirectItemChange = (e) => {
        const itemId = e.target.value;
        formik.setFieldValue("redirect_id", itemId);
        if (formik.values.redirect_type && itemId) {
            formik.setFieldValue("url", `/${formik.values.redirect_type}/${itemId}`);
        }
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <form onSubmit={formik.handleSubmit}>
                <div className="row row-sm">
                    <div className="col-lg-12 col-md-12 animation_fade">
                        <div className="card custom-card">
                            <div className="card-body">
                                <div>
                                    <h6 className="main-content-label mb-3">{t("Add Banners")}</h6>
                                </div>
                                <div className="row row-sm">
                                    <div className="col-lg-12 form-group">
                                        <label htmlFor="banner_image" className="text-left d-flex">
                                            {t("Banner Image")} :<span className="requirestar">*</span>
                                        </label>
                                        <input
                                            name="banner_image"
                                            id="banner_image"
                                            type="file"
                                            accept="image/*"
                                            className="form-control"
                                            onChange={(event) => {
                                                const file = event.currentTarget.files[0];
                                                formik.setFieldValue("banner_image", file);
                                                setPreviewImage(file ? URL.createObjectURL(file) : "");
                                            }}
                                        />
                                        {formik.errors.banner_image && formik.touched.banner_image && (
                                            <span className="text-danger d-flex text-left">{formik.errors.banner_image}</span>
                                        )}
                                        {previewimage && (
                                            <div className="mt-2">
                                                <img src={previewimage} alt="Preview" style={{ height: "100px", borderRadius: "8px" }} />
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-lg-12 form-group">
                                        <label htmlFor="title" className="text-left d-flex">
                                            {t("Title")} :<span className="requirestar">*</span>
                                        </label>
                                        <input
                                            name="title"
                                            id="title"
                                            type="text"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.title}
                                            className="form-control"
                                            placeholder={t("Enter Banner Title")}
                                        />
                                        {formik.errors.title && formik.touched.title && (
                                            <span className="text-danger d-flex text-left">{formik.errors.title}</span>
                                        )}
                                    </div>

                                    <div className="col-lg-12 form-group">
                                        <label htmlFor="description" className="text-left d-flex">
                                            {t("Description")} :<span className="requirestar">*</span>
                                        </label>
                                        <textarea
                                            name="description"
                                            id="description"
                                            rows="4"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.description}
                                            className="form-control"
                                            placeholder={t("Enter Banner Description")}
                                        />
                                        {formik.errors.description && formik.touched.description && (
                                            <span className="text-danger d-flex text-left">{formik.errors.description}</span>
                                        )}
                                    </div>

                                    <div className="col-lg-12 form-group">
                                        <label htmlFor="type" className="text-left d-flex">
                                            {t("Type")} :<span className="requirestar">*</span>
                                        </label>
                                        <select
                                            name="type"
                                            id="type"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.type}
                                            className="form-control"
                                        >
                                            <option value="">{t("Select Type")}</option>
                                            <option value="home">{t("Home")}</option>
                                            <option value="external">{t("External")}</option>
                                            <option value="horoscope">{t("Horoscope")}</option>
                                        </select>
                                        {formik.errors.type && formik.touched.type && (
                                            <span className="text-danger d-flex text-left">{formik.errors.type}</span>
                                        )}
                                    </div>

                                    {/* Redirect Type Dropdown */}
                                    <div className="col-lg-6 form-group">
                                        <label htmlFor="redirect_type" className="text-left d-flex">
                                            {t("Redirect Type")} :
                                        </label>
                                        <select
                                            name="redirect_type"
                                            id="redirect_type"
                                            onChange={handleRedirectTypeChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.redirect_type}
                                            className="form-control"
                                        >
                                            <option value="">-- {t("Select Redirect Type")} --</option>
                                            <option value="puja">Puja</option>
                                            <option value="report">Report</option>
                                            <option value="astrologer">Astrologer</option>
                                            <option value="product">Product</option>
                                            <option value="free_service">Free Service</option>
                                        </select>
                                    </div>

                                    {/* Dependent Redirect Item Dropdown */}
                                    {formik.values.redirect_type && (
                                        <div className="col-lg-6 form-group">
                                            <label htmlFor="redirect_id" className="text-left d-flex">
                                                Select {formik.values.redirect_type === "free_service" ? "Free Service" : formik.values.redirect_type.charAt(0).toUpperCase() + formik.values.redirect_type.slice(1)} :
                                            </label>
                                            <select
                                                name="redirect_id"
                                                id="redirect_id"
                                                onChange={handleRedirectItemChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.redirect_id}
                                                className="form-control"
                                            >
                                                <option value="">-- Select {formik.values.redirect_type === "free_service" ? "Free Service" : formik.values.redirect_type.charAt(0).toUpperCase() + formik.values.redirect_type.slice(1)} --</option>
                                                {formik.values.redirect_type === "puja" && pujas.filter(item => item && (item.title || item.name)).map((item) => (
                                                    <option key={item.id} value={item.id}>{(item.title || item.name).trim()}</option>
                                                ))}
                                                {formik.values.redirect_type === "report" && reports.filter(item => item && (item.title || item.name)).map((item) => (
                                                    <option key={item.id} value={item.id}>{(item.title || item.name).trim()}</option>
                                                ))}
                                                {formik.values.redirect_type === "astrologer" && astrologers.filter(item => item && item.name && item.name.trim() !== "").map((item) => (
                                                    <option key={item.id} value={item.id}>{item.name.trim()}</option>
                                                ))}
                                                {formik.values.redirect_type === "product" && products.filter(item => item && (item.name || item.title)).map((item) => (
                                                    <option key={item.id} value={item.id}>{(item.name || item.title).trim()}</option>
                                                ))}
                                                {formik.values.redirect_type === "free_service" && freeServices.filter(item => item && (item.title || item.name)).map((item) => (
                                                    <option key={item.id} value={item.id}>{(item.title || item.name).trim()}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div className="col-lg-12">
                                        <button
                                            className="btn btn-main-primary signbtn mr-2"
                                            type="submit"
                                            disabled={submitted}
                                        >
                                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                            {t("btn_submit")}
                                        </button>
                                        <button
                                            className="btn ripple btn-secondary"
                                            type="button"
                                            disabled={submitted}
                                            onClick={() => navigate(-1)}
                                        >
                                            <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                            {t("btn_cancel")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
};

export default BannerAdd;
